import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { formatDate, formatMoney, monthBounds } from '../lib/format'
import type { Rate, Role, User } from '../types'

/** Сотрудники и их ставки в одной таблице: ставка принадлежит человеку,
 *  и держать её отдельным списком значило искать одно и то же в двух местах. */
export default function Users() {
  const qc = useQueryClient()
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState<number | null>(null)
  // Сервер отклонил правку — поднимаем счётчик, он входит в key поля,
  // и поле перерисовывается с прежним значением вместо отвергнутого
  const [rollback, setRollback] = useState(0)
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    password: '',
    contact_email: '',
    phone: '',
    role: 'specialist' as Role,
    group_id: '',
  })

  const groups = useQuery({ queryKey: ['groups'], queryFn: () => api.groups() })
  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => api.users(true) })
  const rates = useQuery({ queryKey: ['rates'], queryFn: () => api.rates() })

  const refreshUsers = () => qc.invalidateQueries({ queryKey: ['users'] })
  const refreshRates = () => {
    qc.invalidateQueries({ queryKey: ['rates'] })
    qc.invalidateQueries({ queryKey: ['tasks'] })
    qc.invalidateQueries({ queryKey: ['kpi'] })
    qc.invalidateQueries({ queryKey: ['payout-preview'] })
  }

  const create = useMutation({
    mutationFn: () =>
      api.createUser({
        email: form.email,
        full_name: form.full_name,
        password: form.password,
        contact_email: form.contact_email || null,
        phone: form.phone || null,
        role: form.role,
        group_id: form.group_id ? Number(form.group_id) : null,
      }),
    onSuccess: () => {
      setForm({ ...form, email: '', full_name: '', password: '', contact_email: '', phone: '' })
      refreshUsers()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось создать пользователя'),
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.updateUser(id, body),
    onSuccess: refreshUsers,
    onError: (e: any) => {
      setError(e?.message ?? 'Не удалось сохранить')
      setRollback((n) => n + 1)
    },
  })

  const saveRate = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.updateRate(id, body),
    onSuccess: refreshRates,
    onError: (e: any) => setError(e?.message ?? 'Не удалось изменить ставку'),
  })

  const addRate = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.createRate(body),
    onSuccess: refreshRates,
    onError: (e: any) => setError(e?.message ?? 'Не удалось сохранить ставку'),
  })

  const dropRate = useMutation({
    mutationFn: (id: number) => api.deleteRate(id),
    onSuccess: refreshRates,
    onError: (e: any) => setError(e?.message ?? 'Не удалось удалить ставку'),
  })

  function resetPassword(id: number, name: string) {
    const pwd = prompt(`Новый пароль для «${name}» (минимум 4 символа):`)
    if (pwd && pwd.length >= 4) update.mutate({ id, body: { password: pwd } })
  }

  const all = rates.data ?? []
  /** Базовая — без клиента и проекта; из нескольких берём самую позднюю по дате. */
  const baseRate = (userId: number): Rate | undefined =>
    all
      .filter((r) => r.user_id === userId && !r.client_id && !r.project_id)
      .sort((a, b) => b.valid_from.localeCompare(a.valid_from))[0]
  const specialRates = (userId: number): Rate[] =>
    all.filter((r) => r.user_id === userId && (r.client_id || r.project_id))

  return (
    <>
      {error && (
        <div className="error" onClick={() => setError('')}>
          {error} (нажмите, чтобы скрыть)
        </div>
      )}

      <div className="card section" style={{ padding: 16 }}>
        <h2 style={{ marginTop: 0, fontSize: 15 }}>Новый сотрудник</h2>
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <div className="field">
            <label>Логин</label>
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="petrova"
            />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>ФИО</label>
            <input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Петрова Светлана Алексеевна"
            />
          </div>
          <div className="field">
            <label>Пароль</label>
            <input
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Почта для входа</label>
            <input
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="ivanov@example.ru"
            />
          </div>
          <div className="field">
            <label>Телефон</label>
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+7 999 123-45-67"
            />
          </div>
          <div className="field">
            <label>Группа доступа</label>
            <select
              value={form.group_id}
              onChange={(e) => setForm({ ...form, group_id: e.target.value })}
            >
              <option value="">— не выбрана —</option>
              {groups.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
          <button
            className="btn primary"
            disabled={!form.email || !form.full_name || form.password.length < 4}
            onClick={() => create.mutate()}
          >
            Создать
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Логин</th>
              <th>ФИО</th>
              <th>Для входа по коду</th>
              <th>Группа доступа</th>
              <th className="num">Ставка, ₽/час</th>
              <th>Активен</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.data?.map((u) => {
              const base = baseRate(u.id)
              const special = specialRates(u.id)
              const open = expanded === u.id
              return (
                <RowGroup
                  key={u.id}
                  user={u}
                  base={base}
                  special={special}
                  open={open}
                  rollback={rollback}
                  groups={groups.data ?? []}
                  onToggle={() => setExpanded(open ? null : u.id)}
                  onUpdateUser={(body) => update.mutate({ id: u.id, body })}
                  onResetPassword={() => resetPassword(u.id, u.full_name)}
                  onSaveRate={(id, body) => saveRate.mutate({ id, body })}
                  onAddRate={(body) => addRate.mutate({ user_id: u.id, ...body })}
                  onDropRate={(id) => dropRate.mutate(id)}
                />
              )
            })}
          </tbody>
        </table>
      </div>

      <p style={{ color: 'var(--muted)' }}>
        Ставка без клиента — базовая, она и стоит в таблице. Ставки под отдельных клиентов
        заводятся кнопкой «Ставки» в строке сотрудника: при расчёте выигрывает более
        частная — по проекту, затем по клиенту, затем базовая. Прошлые ведомости не
        пересчитываются: в них ставка зафиксирована на момент формирования.
      </p>
    </>
  )
}

interface RowProps {
  user: User
  base?: Rate
  special: Rate[]
  open: boolean
  rollback: number
  groups: { id: number; name: string }[]
  onToggle: () => void
  onUpdateUser: (body: Record<string, unknown>) => void
  onResetPassword: () => void
  onSaveRate: (id: number, body: Record<string, unknown>) => void
  onAddRate: (body: Record<string, unknown>) => void
  onDropRate: (id: number) => void
}

/** Строка сотрудника плюс раскрывающийся блок с его ставками по клиентам. */
function RowGroup({
  user,
  base,
  special,
  open,
  rollback,
  groups,
  onToggle,
  onUpdateUser,
  onResetPassword,
  onSaveRate,
  onAddRate,
  onDropRate,
}: RowProps) {
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.clients(), enabled: open })
  const [draft, setDraft] = useState({ client_id: '', amount: '', valid_from: monthBounds().from })
  const [newBase, setNewBase] = useState('')

  return (
    <>
      <tr className={open ? 'running' : undefined}>
        {/* Логин и ФИО правятся на месте: значение уходит на сервер при уходе из поля,
            пустое — откатывается обратно, чтобы не стереть имя случайным Ctrl+A. */}
        <td className="mono" data-label="Логин">
          <input
            className="cell-input mono"
            key={`email-${user.id}-${user.email}-${rollback}`}
            defaultValue={user.email}
            onBlur={(e) => {
              const v = e.target.value.trim()
              if (!v) e.target.value = user.email
              else if (v !== user.email) onUpdateUser({ email: v })
            }}
          />
        </td>
        <td data-label="ФИО">
          <input
            className="cell-input client-name"
            key={`name-${user.id}-${user.full_name}-${rollback}`}
            defaultValue={user.full_name}
            onBlur={(e) => {
              const v = e.target.value.trim()
              if (!v) e.target.value = user.full_name
              else if (v !== user.full_name) onUpdateUser({ full_name: v })
            }}
          />
        </td>
        <td data-label="Для входа по коду" className="cell-contacts">
          <input
            className="cell-input"
            key={`mail-${user.id}-${user.contact_email ?? ''}-${rollback}`}
            defaultValue={user.contact_email ?? ''}
            placeholder="почта"
            onBlur={(e) => {
              const v = e.target.value.trim()
              if (v !== (user.contact_email ?? '')) onUpdateUser({ contact_email: v || null })
            }}
          />
          <input
            className="cell-input mono"
            key={`tel-${user.id}-${user.phone ?? ''}-${rollback}`}
            defaultValue={user.phone ?? ''}
            placeholder="телефон"
            onBlur={(e) => {
              const v = e.target.value.trim()
              if (v !== (user.phone ?? '')) onUpdateUser({ phone: v || null })
            }}
          />
        </td>
        <td data-label="Группа доступа">
          <select
            value={user.group_id ?? ''}
            onChange={(e) =>
              onUpdateUser({ group_id: e.target.value ? Number(e.target.value) : null })
            }
          >
            <option value="">— не выбрана —</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </td>
        <td className="num" data-label="Ставка">
          {base ? (
            <input
              type="number"
              step="0.01"
              min="0"
              style={{ width: 100, textAlign: 'right' }}
              defaultValue={Number(base.amount_per_hour)}
              title={`Действует с ${formatDate(base.valid_from)}`}
              onBlur={(e) => {
                const v = Number(e.target.value)
                if (v >= 0 && v !== Number(base.amount_per_hour))
                  onSaveRate(base.id, { amount_per_hour: v })
              }}
            />
          ) : (
            <span className="rate-empty">
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="нет"
                style={{ width: 100, textAlign: 'right' }}
                value={newBase}
                onChange={(e) => setNewBase(e.target.value)}
                onBlur={(e) => {
                  const v = Number(e.target.value)
                  if (v > 0) {
                    onAddRate({ amount_per_hour: v, valid_from: monthBounds().from })
                    setNewBase('')
                  }
                }}
              />
            </span>
          )}
        </td>
        <td data-label="Активен">
          <input
            type="checkbox"
            checked={user.is_active}
            onChange={(e) => onUpdateUser({ is_active: e.target.checked })}
          />
        </td>
        <td className="cell-actions" data-label="">
          <button className="btn small" onClick={onToggle}>
            Ставки{special.length > 0 ? ` (${special.length})` : ''}
          </button>{' '}
          <button className="btn small" onClick={onResetPassword}>
            Сменить пароль
          </button>
        </td>
      </tr>

      {open && (
        <tr className="subrow">
          <td colSpan={6}>
            <div className="rate-panel">
              <div className="rate-panel-title">
                Ставки: {user.full_name}
                {base && (
                  <span className="rate-panel-base">
                    базовая {formatMoney(base.amount_per_hour)}/ч с {formatDate(base.valid_from)}
                  </span>
                )}
              </div>

              {special.length > 0 && (
                <table className="rate-table">
                  <thead>
                    <tr>
                      <th>Клиент</th>
                      <th>Проект</th>
                      <th className="num">₽/час</th>
                      <th>Действует с</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {special.map((r) => (
                      <tr key={r.id}>
                        <td>{r.client_name ?? 'все'}</td>
                        <td>{r.project_name ?? 'все'}</td>
                        <td className="num">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            style={{ width: 100, textAlign: 'right' }}
                            defaultValue={Number(r.amount_per_hour)}
                            onBlur={(e) => {
                              const v = Number(e.target.value)
                              if (v >= 0 && v !== Number(r.amount_per_hour))
                                onSaveRate(r.id, { amount_per_hour: v })
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            defaultValue={r.valid_from}
                            onBlur={(e) => {
                              if (e.target.value && e.target.value !== r.valid_from)
                                onSaveRate(r.id, { valid_from: e.target.value })
                            }}
                          />
                        </td>
                        <td>
                          <button className="btn small danger" onClick={() => onDropRate(r.id)}>
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="toolbar" style={{ marginBottom: 0, marginTop: 10 }}>
                <div className="field">
                  <label>Клиент</label>
                  <select
                    value={draft.client_id}
                    onChange={(e) => setDraft({ ...draft, client_id: e.target.value })}
                  >
                    <option value="">Базовая ставка</option>
                    {clients.data?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>₽ в час</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={{ width: 110 }}
                    value={draft.amount}
                    onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Действует с</label>
                  <input
                    type="date"
                    value={draft.valid_from}
                    onChange={(e) => setDraft({ ...draft, valid_from: e.target.value })}
                  />
                </div>
                <button
                  className="btn primary"
                  disabled={!draft.amount}
                  onClick={() => {
                    onAddRate({
                      client_id: draft.client_id ? Number(draft.client_id) : null,
                      amount_per_hour: Number(draft.amount),
                      valid_from: draft.valid_from,
                    })
                    setDraft({ ...draft, amount: '' })
                  }}
                >
                  Добавить ставку
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
